import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { SHIPPING_OPTIONS, PRODUCT } from "@/data/product";
import { api, formatErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Trash2, Minus, Plus, ShoppingBag, Loader2, Tag, X } from "lucide-react";
import { toast } from "sonner";

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
}

export default function CartDrawer() {
  const { items, isOpen, close, shipping, setShipping, updateQty, removeItem, promoCode, applyPromo, clearPromo } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const shippingOpt = SHIPPING_OPTIONS.find((s) => s.id === shipping) || SHIPPING_OPTIONS[0];
  const subtotal = items.reduce((s, i) => s + i.quantity * PRODUCT.price, 0);
  const discount = promoCode ? Math.round(subtotal * (promoCode.percent_off / 100) * 100) / 100 : 0;
  const total = Math.max(0.5, subtotal - discount + shippingOpt.price);

  const handleApplyPromo = async (e) => {
    e?.preventDefault?.();
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", { code });
      applyPromo(data);
      toast.success(`Code ${data.code} appliqué — ${data.label}`);
      setPromoInput("");
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Code invalide");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      trackEvent("InitiateCheckout", {
        num_items: items.reduce((s, i) => s + i.quantity, 0),
        value: total,
        currency: "EUR",
        content_ids: items.map((i) => i.colorId),
      });
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({
          colorId: i.colorId,
          colorName: i.colorName,
          colorCode: i.colorCode,
          length: "24\"",
          quantity: i.quantity,
        })),
        shipping,
        email: email || null,
        promo_code: promoCode?.code || null,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Impossible de démarrer le paiement.");
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(v) => (v ? null : close())}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FDFBF9] border-l border-[#EADBD1] text-[#2C2420] p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#EADBD1]">
          <SheetTitle className="font-serif text-2xl text-[#2C2420] flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-[#C58C85]" /> Votre panier
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <ShoppingBag className="h-12 w-12 text-[#EADBD1] mb-4" />
              <p className="text-[#6B5D54] text-sm">Votre panier est vide.</p>
              <Button onClick={close} className="mt-6 rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white" data-testid="cart-keep-shopping">
                Continuer mes achats
              </Button>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <li key={i.colorId} className="flex gap-3 items-start" data-testid={`cart-item-${i.colorId}`}>
                  <div
                    className="h-16 w-16 rounded-xl border border-[#EADBD1] flex-shrink-0"
                    style={{ background: i.swatch }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#2C2420] truncate">{i.colorName}</p>
                    <p className="text-[11px] text-[#6B5D54] font-mono">{i.colorCode} · 24&quot;</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 rounded-full border border-[#EADBD1] bg-white px-1 py-0.5">
                        <button
                          onClick={() => updateQty(i.colorId, Math.max(0, i.quantity - 1))}
                          className="h-7 w-7 rounded-full hover:bg-[#F5EFE6] grid place-items-center"
                          data-testid={`cart-dec-${i.colorId}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-xs w-5 text-center" data-testid={`cart-qty-${i.colorId}`}>{i.quantity}</span>
                        <button
                          onClick={() => updateQty(i.colorId, Math.min(50, i.quantity + 1))}
                          className="h-7 w-7 rounded-full hover:bg-[#F5EFE6] grid place-items-center"
                          data-testid={`cart-inc-${i.colorId}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-sm font-medium text-[#2C2420]">{formatEUR(i.quantity * PRODUCT.price)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(i.colorId)}
                    className="text-[#6B5D54] hover:text-[#2C2420] mt-1"
                    aria-label="Retirer"
                    data-testid={`cart-remove-${i.colorId}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#EADBD1] bg-white px-6 py-5 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-2">Livraison</p>
              <div className="grid grid-cols-1 gap-1.5">
                {SHIPPING_OPTIONS.map((s) => (
                  <label
                    key={s.id}
                    className={`flex items-center justify-between rounded-xl border px-3 py-2 cursor-pointer transition-colors ${
                      shipping === s.id ? "border-[#2C2420] bg-[#2C2420]/5" : "border-[#EADBD1] hover:border-[#C58C85]"
                    }`}
                    data-testid={`cart-shipping-${s.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="shipping"
                        checked={shipping === s.id}
                        onChange={() => setShipping(s.id)}
                        className="accent-[#2C2420]"
                      />
                      <span className="text-sm">{s.label}</span>
                    </div>
                    <span className={`text-sm font-medium ${s.price === 0 ? "text-[#25D366]" : "text-[#2C2420]"}`}>
                      {s.price === 0 ? "Gratuit" : formatEUR(s.price)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <input
                type="email"
                placeholder="Email (recommandé pour le suivi)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 rounded-full bg-[#FDFBF9] border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm placeholder:text-[#6B5D54]/60"
                data-testid="cart-email"
              />
            </div>

            <Separator className="bg-[#EADBD1]" />

            {/* Promo code */}
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-2 flex items-center gap-1.5">
                <Tag className="h-3 w-3" /> Code promo
              </p>
              {promoCode ? (
                <div className="flex items-center justify-between rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 px-3 py-2.5" data-testid="cart-promo-applied">
                  <div>
                    <p className="text-sm font-medium text-[#2C2420]"><span className="font-mono">{promoCode.code}</span> · −{promoCode.percent_off}%</p>
                    <p className="text-[11px] text-[#6B5D54]">{promoCode.label}</p>
                  </div>
                  <button
                    onClick={() => { clearPromo(); toast.info("Code promo retiré"); }}
                    className="text-[#6B5D54] hover:text-[#2C2420]"
                    aria-label="Retirer le code"
                    data-testid="cart-promo-remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApplyPromo} className="flex gap-2" data-testid="cart-promo-form">
                  <input
                    type="text"
                    placeholder="WELCOME10"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-full bg-[#FDFBF9] border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm uppercase tracking-wider placeholder:text-[#6B5D54]/50"
                    data-testid="cart-promo-input"
                  />
                  <Button
                    type="submit"
                    disabled={promoLoading || !promoInput.trim()}
                    variant="outline"
                    className="rounded-full border-[#2C2420] hover:bg-[#2C2420] hover:text-white h-10 px-4 text-sm"
                    data-testid="cart-promo-apply-btn"
                  >
                    {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Appliquer"}
                  </Button>
                </form>
              )}
            </div>

            <Separator className="bg-[#EADBD1]" />

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-[#6B5D54]"><span>Sous-total</span><span>{formatEUR(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-[#25D366]" data-testid="cart-discount-line">
                  <span>Remise ({promoCode.code})</span>
                  <span>−{formatEUR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-[#6B5D54]"><span>{shippingOpt.label}</span><span>{shippingOpt.price === 0 ? "Gratuit" : formatEUR(shippingOpt.price)}</span></div>
              <div className="flex justify-between text-[#2C2420] font-medium pt-1.5 border-t border-[#EADBD1]">
                <span>Total</span>
                <span className="font-serif text-xl" data-testid="cart-total">{formatEUR(total)}</span>
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white h-12"
              data-testid="cart-checkout-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection…</>
              ) : (
                <>Payer en ligne · {formatEUR(total)}</>
              )}
            </Button>
            <p className="text-[11px] text-[#6B5D54] text-center">
              Paiement sécurisé par Stripe · Apple Pay & Google Pay acceptés
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
